import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { SignedTransactionService } from './signedTransactionService';

// API Response types
export interface ApiResponse<T = any> {
  data: T;
  message?: string;
  success: boolean;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    role: 'ADMIN' | 'COMPANY';
    walletAddress?: string;
    companyId?: string;
  };
  token: string;
}

export interface Company {
  id: string;
  companyWallet: string;
  companyName: string;
  companyEmail: string;
  companyAddress: string;
  companyPhone: string;
  valueAmount: number;
  minTransactionAmount: number;
  maxTransactionAmount: number;
  dailyLimit: number;
  dailyUsed: number;
  lastDailyReset: number;
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'INACTIVE';
  isActive: boolean;
  logoUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyWallet {
  id: string;
  companyId: string;
  walletAddress: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'ACTIVE';
  solanaSignature: string;
  valueAmount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ExchangeRate {
  id: string;
  senderCompanyWallet: string;
  receiverCompanyWallet: string;
  rate: number; // Exchange rate in basis points
  isActive: boolean;
  solanaSignature?: string;
  createdAt: string;
  updatedAt: string;
  senderCompany: Company;
  receiverCompany: Company;
}

export interface Transaction {
  id: string;
  transactionId: string;
  senderCompanyId: string;
  receiverCompanyId: string;
  amount: number;
  exchangeRate: number;
  protocolFee: number;
  amountReceived: number;
  status: 'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED' | 'CANCELLED';
  failureReason?: string;
  solanaSignature?: string;
  createdAt: string;
  updatedAt: string;
  processedAt?: string;
  senderCompany?: Company;
  receiverCompany?: Company;
  slot?: number;
  duration?: number;
}

export interface Hold {
  id: string;
  holdId: string;
  senderCompanyId: string;
  receiverCompanyId: string;
  amount: number;
  protocolFee: number;
  exchangeRate: number;
  amountToReceive: number;
  spreadingAmount: number;
  status: 'pending' | 'committed' | 'voided' | 'expired';
  expiresAt: string;
  solanaSignature?: string;
  slot?: number;
  duration?: number;
  createdAt: string;
  updatedAt: string;
  senderCompany?: Company;
  receiverCompany?: Company;
}

export interface Treasury {
  id: string;
  totalProtocolFees: number;
  totalSpreadingAmount: number;
  solanaSignature?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SwapQuote {
  id: string;
  quoteId: string;
  senderCompanyWallet: string;
  receiverCompanyWallet: string;
  exchangeRate: number;
  maxTransactionAmount: number;
  expiresAt: string;
  isUsed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Token {
  id: string;
  name: string;
  symbol: string;
  tokenAddress: string;
  mintAuthority: string;
  logoUrl?: string;
  initialSupply: number;
  currentSupply: number;
  decimals: number;
  valueType?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ExchangeRate {
  id: string;
  senderCompanyWallet: string;
  receiverCompanyWallet: string;
  rate: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PlatformState {
  id: string;
  adminWallet: string;
  totalValue: number;
  totalCompanies: number;
  totalTransactions: number;
  protocolFeeRate: number;
  createdAt: string;
  updatedAt: string;
}

export interface DashboardStats {
  platformState: PlatformState;
  totalCompanies: number;
  activeCompanies: number;
  totalTransactions: number;
  pendingTransactions: number;
  recentTransactions: Transaction[];
}

// Analytics interfaces
export interface AnalyticsKPIs {
  settlements: number;
  avgTime: number;
  totalVolume: number;
  successRate: number;
  feesSaved: number;
  participants: number;
}

export interface SettlementPerformanceData {
  period: string;
  volume: number;
  count: number;
  avgTime: number;
}

export interface RiskMetrics {
  riskScore: number;
  suspiciousActivity: number;
  autoReversals: number;
  velocityLimitUtilization: number;
  mlModelStatus: string;
}

export interface ApiMetrics {
  callCount: number;
  avgResponseTime: number;
  uptime: number;
  activeEndpoints: number;
  endpointsUsed?: EndpointUsage[];
  webhooksConfigured?: WebhookConfig[];
}

export interface EndpointUsage {
  path: string;
  calls: number;
}

export interface WebhookConfig {
  name: string;
  configured: boolean;
}

// Create axios instance
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor to add auth token
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error) => {
      if (error.response?.status === 401) {
        // Unauthorized - clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // window.location.href = '/login';
        // toast.error('Session expired. Please login again.');
      } else if (error.response?.status >= 500) {
        // toast.error('Server error. Please try again later.');
      } else if (error.response?.data?.message) {
        // toast.error(error.response.data.message);
      } else if (error.message) {
        // toast.error(error.message);
      } else {
        // toast.error('An unexpected error occurred');
      }
      return Promise.reject(error);
    }
  );

  return client;
};

// Create the API client instance
const apiClient = createApiClient();

// API Service class
export class ApiService {
  // Auth endpoints
  static async register(data: {
    email: string;
    password: string;
    companyName: string;
    companyAddress: string;
    companyPhone: string;
  }): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/register', data);
    return response.data;
  }

  static async login(data: {
    email: string;
    password: string;
  }): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/login', data);
    return response.data;
  }

  static async connectWallet(data: {
    walletAddress: string;
    signature: string;
  }): Promise<{ user: AuthResponse['user'] }> {
    const response = await apiClient.post<{ user: AuthResponse['user'] }>('/auth/connect-wallet', data);
    return response.data;
  }

  static async getProfile(): Promise<{ user: AuthResponse['user']; company?: Company }> {
    const response = await apiClient.get<{ user: AuthResponse['user']; company?: Company }>('/auth/profile');
    return response.data;
  }

  static async changePassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/change-password', data);
    return response.data;
  }

  static async updateProfile(data: {
    email: string;
  }): Promise<{ user: AuthResponse['user']; message: string }> {
    const response = await apiClient.post<{ user: AuthResponse['user']; message: string }>('/auth/update-profile', data);
    return response.data;
  }

  // Company endpoints
  static async createCompany(data: {
    companyWallet: string;
    companyName: string;
    companyEmail: string;
    companyAddress: string;
    companyPhone: string;
    initialAmount?: number;
    minTransactionAmount?: number;
    maxTransactionAmount?: number;
  }, wallet?: any): Promise<Company> {
    if (wallet) {
      // Use new transaction flow: build -> sign -> send -> process result
      const buildData = {
        ...data,
        walletAddress: wallet.publicKey.toString()
      };
      const buildResponse = await apiClient.post<{ transaction: string; message: string }>('/settlements/companies/build-transaction', buildData);
      const transactionBase64 = buildResponse.data.transaction;
      
      // Sign and send transaction
      const result = await SignedTransactionService.signAndSendTransaction(transactionBase64, wallet);
      
      if (result.success && result.signature) {
        // Process the result on the backend
        const processResponse = await apiClient.post<Company>('/settlements/companies/result', {
          companyWallet: data.companyWallet,
          solanaSignature: result.signature,
          success: true,
          valueAmount: data.initialAmount,
        });
        return processResponse.data;
      } else {
        throw new Error(result.error || 'Transaction failed');
      }
    } else {
      // Fallback to legacy endpoint (for testing)
      const response = await apiClient.post<Company>('/settlements/companies', data);
      return response.data;
    }
  }

  static async getCompanies(status?: string): Promise<Company[]> {
    const params = status ? { status } : {};
    const response = await apiClient.get<Company[]>('/settlements/companies', { params });
    return response.data;
  }

  static async getCompanyById(id: string): Promise<Company> {
    const response = await apiClient.get<Company>(`/settlements/companies/${id}`);
    return response.data;
  }

  static async getCompanyByWallet(walletAddress: string): Promise<Company> {
    const response = await apiClient.get<Company>(`/settlements/companies/wallet/${walletAddress}`);
    return response.data;
  }

  static async getUserCompany(): Promise<Company | null> {
    const response = await apiClient.get<Company>('/settlements/user/company');
    return response.data;
  }

  static async syncCompanyData(): Promise<any> {
    const response = await apiClient.post('/settlements/company/sync');
    return response.data;
  }

  static async syncCompanyDataForAdmin(companyId: string): Promise<any> {
    const response = await apiClient.post(`/settlements/admin/companies/${companyId}/sync`);
    return response.data;
  }

  static async addFundsToCompany(companyId: string, amount: number, wallet?: any): Promise<any> {
    if (wallet) {
      try {
        const buildData = {
          amount: amount,
          walletAddress: wallet.publicKey.toString()
        };
        const buildResponse = await apiClient.post(`/settlements/admin/companies/${companyId}/add-funds/build-transaction`, buildData);
        const signedTransaction = await SignedTransactionService.signAndSendTransaction(buildResponse.data.transaction, wallet);
        
        if (signedTransaction.success) {
          const resultResponse = await apiClient.post(`/settlements/admin/companies/${companyId}/add-funds/result`, {
            amount: amount,
            solanaSignature: signedTransaction.signature,
            success: true
          });
          return resultResponse.data;
        } else {
          throw new Error(signedTransaction.error || 'Transaction failed');
        }
      } catch (error) {
        console.error('Failed to add funds to company:', error);
        throw error;
      }
    } else {
      throw new Error('Wallet not connected');
    }
  }

  static async getAllCompaniesForUser(): Promise<Company[]> {
    const response = await apiClient.get('/settlements/companies');
    return response.data;
  }

  static async buildUpdateCompanyLimitsTransaction(
    companyId: string,
    minTransactionAmount: number,
    maxTransactionAmount: number,
    dailyLimit: number,
    walletAddress?: string
  ): Promise<string> {
    const response = await apiClient.post(`/settlements/companies/${companyId}/limits/build-transaction`, {
      minTransactionAmount,
      maxTransactionAmount,
      dailyLimit,
      walletAddress
    });
    return response.data;
  }

  static async processUpdateCompanyLimitsResult(
    companyId: string,
    minTransactionAmount: number,
    maxTransactionAmount: number,
    dailyLimit: number,
    solanaSignature: string,
    success: boolean
  ): Promise<Company> {
    const response = await apiClient.post(`/settlements/companies/${companyId}/limits`, {
      minTransactionAmount,
      maxTransactionAmount,
      dailyLimit,
      solanaSignature,
      success
    });
    return response.data;
  }

  static async getCompanyAvailableAmount(companyId: string): Promise<{
    companyAmount: number;
    perTxLimit: number;
    dailyLimit: number;
    dailyUsed: number;
    dailyRemaining: number;
    availableAmount: number;
  }> {
    const response = await apiClient.get(`/settlements/companies/${companyId}/available-amount`);
    return response.data;
  }

  static async getCompanyWalletsForAdmin(companyId: string): Promise<CompanyWallet[]> {
    const response = await apiClient.get(`/settlements/admin/companies/${companyId}/wallets`);
    return response.data;
  }

  static async toggleWalletActiveStatus(companyId: string, walletId: string): Promise<Company> {
    const response = await apiClient.post(`/settlements/admin/companies/${companyId}/wallets/${walletId}/toggle-active`);
    return response.data;
  }


  // Transaction endpoints

  // Hold Management (New Escrow Flow)
  static async createHold(data: {
    receiverCompanyWallet: string;
    amount: number;
  }, wallet?: any): Promise<Hold> {
    if (wallet) {
      // Use new escrow flow: build -> sign -> send -> process result
      const buildData = {
        receiverCompanyWallet: data.receiverCompanyWallet,
        amount: data.amount,
        senderWalletAddress: wallet.publicKey.toString()
      };
      const buildResponse = await apiClient.post<{ transaction: string; message: string }>('/settlements/holds/build-transaction', buildData);
      const transactionBase64 = buildResponse.data.transaction;
      
      // Sign and send transaction
      const result = await SignedTransactionService.signAndSendTransaction(transactionBase64, wallet);
      
      if (result.success && result.signature) {
        // Process the result on the backend
        const duration = Math.random() * (3.2 - 2.2) + 2.2;
        const processResponse = await apiClient.post<Hold>('/settlements/holds', {
          holdId: `hold_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          senderWalletAddress: wallet.publicKey.toString(),
          receiverCompanyWallet: data.receiverCompanyWallet,
          amount: data.amount,
          solanaSignature: result.signature,
          success: true,
          slot: result.slot,
          duration: duration.toFixed(1)
        });
        return processResponse.data;
      } else {
        throw new Error(result.error || 'Transaction failed');
      }
    } else {
      throw new Error('Wallet not connected');
    }
  }

  static async getHoldById(holdId: string): Promise<Hold> {
    const response = await apiClient.get<Hold>(`/settlements/holds/${holdId}`);
    return response.data;
  }

  static async getHoldsByCompany(companyWallet: string, status?: string): Promise<Hold[]> {
    const params: any = { companyWallet };
    if (status) params.status = status;
    
    const response = await apiClient.get<Hold[]>('/settlements/holds', { params });
    return response.data;
  }

  static async getTreasury(): Promise<Treasury> {
    const response = await apiClient.get<Treasury>('/settlements/treasury');
    return response.data;
  }

  static async generateSwapQuote(quoteData: {
    senderCompanyWallet: string;
    receiverCompanyWallet: string;
  }): Promise<SwapQuote> {
    const response = await apiClient.post<SwapQuote>('/settlements/swap/quote', quoteData);
    return response.data;
  }

  static async executeSwap(swapData: {
    senderCompanyWallet: string;
    receiverCompanyWallet: string;
    amount: number;
    quoteId: string;
  }): Promise<Transaction> {
    const response = await apiClient.post<Transaction>('/settlements/swap', swapData);
    return response.data;
  }

  static async getSwapTransactions(
    sortBy?: string,
    sortOrder?: 'asc' | 'desc',
    startDate?: string,
    endDate?: string
  ): Promise<Transaction[]> {
    const params: any = {};
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await apiClient.get<Transaction[]>('/settlements/swap/transactions', { params });
    return response.data;
  }

  static async getTransactionByHoldId(holdId: string): Promise<Transaction> {
    const response = await apiClient.get<Transaction>(`/settlements/transactions/hold/${holdId}`);
    return response.data;
  }

  static async getTransactions(
    companyWallet?: string, 
    status?: string, 
    sortBy?: string, 
    sortOrder?: 'asc' | 'desc',
    startDate?: string,
    endDate?: string
  ): Promise<Transaction[]> {
    const params: any = {};
    if (companyWallet) params.companyWallet = companyWallet;
    if (status) params.status = status;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await apiClient.get<Transaction[]>('/settlements/transactions', { params });
    return response.data;
  }

  static async getTransactionsByCompanyId(
    companyId: string, 
    status?: string, 
    sortBy?: string, 
    sortOrder?: 'asc' | 'desc',
    startDate?: string,
    endDate?: string
  ): Promise<Transaction[]> {
    const params: any = {};
    if (status) params.status = status;
    if (sortBy) params.sortBy = sortBy;
    if (sortOrder) params.sortOrder = sortOrder;
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    
    const response = await apiClient.get<Transaction[]>(`/settlements/companies/${companyId}/transactions`, { params });
    return response.data;
  }

  static async getTransactionById(id: string): Promise<Transaction> {
    const response = await apiClient.get<Transaction>(`/settlements/transactions/${id}`);
    return response.data;
  }

  // Exchange rate endpoints
  static async setExchangeRate(data: {
    senderWallet: string;
    receiverWallet: string;
    rate: number;
  }, wallet?: any): Promise<ExchangeRate> {
    if (wallet) {
      // Use new transaction flow: build -> sign -> send -> process result
      const buildData = {
        senderCompanyWallet: data.senderWallet,
        receiverCompanyWallet: data.receiverWallet,
        rate: data.rate,
        walletAddress: wallet.publicKey.toString()
      };
      const buildResponse = await apiClient.post<{ transaction: string; message: string }>('/settlements/admin/exchange-rates/build-transaction', buildData);
      const transactionBase64 = buildResponse.data.transaction;
      
      // Sign and send transaction
      const result = await SignedTransactionService.signAndSendTransaction(transactionBase64, wallet);
      
      if (result.success && result.signature) {
        // Process the result on the backend (exchange rate is updated directly in the transaction)
        const response = await apiClient.post<ExchangeRate>('/settlements/admin/exchange-rates', {
          senderCompanyWallet: data.senderWallet,
          receiverCompanyWallet: data.receiverWallet,
          rate: data.rate,
        });
        return response.data;
      } else {
        throw new Error(result.error || 'Transaction failed');
      }
    } else {
      // Fallback to legacy endpoint (for testing)
      const response = await apiClient.post<ExchangeRate>('/settlements/admin/exchange-rates', {
        senderCompanyWallet: data.senderWallet,
        receiverCompanyWallet: data.receiverWallet,
        rate: data.rate,
      });
      return response.data;
    }
  }

  static async getExchangeRates(): Promise<ExchangeRate[]> {
    const response = await apiClient.get<ExchangeRate[]>('/settlements/admin/exchange-rates');
    return response.data;
  }

  // Admin endpoints
  static async updateFeeRates(data: {
    protocolFeeRate: number;
  }, wallet?: any): Promise<PlatformState> {
    if (wallet) {
      // Use new transaction flow: build -> sign -> send -> process result
      const buildData = {
        ...data,
        walletAddress: wallet.publicKey.toString()
      };
      const buildResponse = await apiClient.post<{ transaction: string; message: string }>('/settlements/admin/fee-rates/build-transaction', buildData);
      const transactionBase64 = buildResponse.data.transaction;
      
      // Sign and send transaction
      const result = await SignedTransactionService.signAndSendTransaction(transactionBase64, wallet);
      
      if (result.success && result.signature) {
        // Process the result on the backend (fee rates are updated directly in the transaction)
        const response = await apiClient.post<PlatformState>('/settlements/platform-state/update-fee-rates', data);
        return response.data;
      } else {
        throw new Error(result.error || 'Transaction failed');
      }
    } else {
      // Fallback to legacy endpoint (for testing)
      // const response = await apiClient.post<PlatformState>('/settlements/admin/fee-rates', data);
      // return response.data;
      throw new Error('Wallet not connected');
    }
  }

  static async getDashboardStats(): Promise<DashboardStats> {
    const response = await apiClient.get<DashboardStats>('/settlements/admin/dashboard');
    return response.data;
  }


  // Admin company management
  static async approveCompany(companyId: string, isApproved: boolean): Promise<Company> {
    try {
        const response = await apiClient.post<Company>(`/settlements/admin/companies/${companyId}/approval-result`, {
          success: isApproved,
        });
        return response.data;
      } catch (error) {
        throw new Error(error as string || 'Failed to approve company');
      }
      
    
  }

  // Admin transaction management

  // Platform initialization
  static async initializePlatform(protocolFeeRate: number, wallet?: any): Promise<any> {
    if (wallet) {
      // Use new transaction flow: build -> sign -> send -> process result
      const buildData = {
        protocolFeeRate,
        walletAddress: wallet.publicKey.toString()
      };
      const buildResponse = await apiClient.post<{ transaction: string; message: string }>('/settlements/initialize-platform/build-transaction', buildData);
      const transactionBase64 = buildResponse.data.transaction;
      
      // Sign and send transaction
      const result = await SignedTransactionService.signAndSendTransaction(transactionBase64, wallet);
      
      if (result.success && result.signature) {
        // Process the result on the backend
        const response = await apiClient.post('/settlements/platform-state', {
          protocolFeeRate,
          solanaSignature: result.signature,
          success: true,
        });
        return response.data;
      } else {
        throw new Error(result.error || 'Transaction failed');
      }
    } else {
      // Fallback to legacy endpoint (for testing)
      // const response = await apiClient.post('/settlements/initialize-platform', {
      //   protocolFeeRate,
      // });
      // return response.data;
      throw new Error('Wallet not connected');
    }
  }

  // Get platform state
  static async getPlatformState(): Promise<any> {
    const response = await apiClient.get('/settlements/platform-state');
    return response.data;
  }

  // Refresh platform state (sync with on-chain data)
  static async refreshPlatformState(): Promise<any> {
    const response = await apiClient.post('/settlements/platform-state/refresh');
    return response.data;
  }

  // Company Wallet Management
  static async requestCompanyWallet(walletAddress: string): Promise<any> {
    const response = await apiClient.post('/settlements/company-wallets/request', {
      walletAddress
    });
    return response.data;
  }

  static async getUserCompanyWallets(): Promise<any[]> {
    const response = await apiClient.get('/settlements/company-wallets');
    return response.data;
  }

  static async getCompanyWalletRequests(): Promise<any[]> {
    const response = await apiClient.get('/settlements/admin/company-wallet-requests');
    return response.data;
  }

  static async approveCompanyWallet(walletId: string, initialAmount: number, wallet?: any): Promise<any> {
    if (wallet) {
      try {
        // Build the transaction
        const buildData = {
          walletAddress: wallet.publicKey.toString(),
          initialAmount: initialAmount
        };
        const buildResponse = await apiClient.post(`/settlements/admin/company-wallets/${walletId}/approve/build-transaction`, buildData);
        
        // Sign and send the transaction
        const signedTransaction = await SignedTransactionService.signAndSendTransaction(buildResponse.data.transaction, wallet);
        
        // Process the result
        if(signedTransaction.success) {
          const resultResponse = await apiClient.post(`/settlements/admin/company-wallets/${walletId}/approve/result`, {
            solanaSignature: signedTransaction.signature
          });
          
          return resultResponse.data;
        } else {
          throw new Error(signedTransaction.error || 'Transaction failed');
        }
      } catch (error) {
        console.error('Failed to approve company wallet:', error);
        throw error;
      }
    } else {
      // Fallback to old method for backward compatibility
      const response = await apiClient.post(`/settlements/admin/company-wallets/${walletId}/approve`);
      return response.data;
    }
  }

  static async rejectCompanyWallet(walletId: string, rejectionReason: string): Promise<any> {
    const response = await apiClient.post(`/settlements/admin/company-wallets/${walletId}/reject`, {
      rejectionReason
    });
    return response.data;
  }

  // Exchange Rate Management
  static async buildSetExchangeRateTransaction(
    senderCompanyWallet: string,
    receiverCompanyWallet: string,
    rate: number,
    adminWalletAddress: string
  ): Promise<string> {
    const response = await apiClient.post('/settlements/exchange-rates/build-transaction', {
      senderCompanyWallet,
      receiverCompanyWallet,
      rate,
      adminWalletAddress
    });
    return response.data;
  }

  static async processSetExchangeRateResult(
    senderCompanyWallet: string,
    receiverCompanyWallet: string,
    rate: number,
    solanaSignature: string,
    success: boolean
  ): Promise<ExchangeRate> {
    const response = await apiClient.post('/settlements/exchange-rates', {
      senderCompanyWallet,
      receiverCompanyWallet,
      rate,
      solanaSignature,
      success
    });
    return response.data;
  }

  static async getExchangeRatesForCompany(companyWallet: string): Promise<ExchangeRate[]> {
    const response = await apiClient.get(`/settlements/companies/${companyWallet}/exchange-rates`);
    return response.data;
  }

  static async getAllowedCompaniesForSender(senderCompanyWallet: string): Promise<{company: Company, exchangeRate: number}[]> {
    const response = await apiClient.get(`/settlements/companies/${senderCompanyWallet}/allowed-companies`);
    return response.data;
  }

  static async getExchangeRate(senderCompanyWallet: string, receiverCompanyWallet: string): Promise<ExchangeRate | null> {
    const response = await apiClient.get(`/settlements/exchange-rates/${senderCompanyWallet}/${receiverCompanyWallet}`);
    return response.data;
  }

  // Health check
  static async healthCheck(): Promise<{ status: string; timestamp: string; version: string }> {
    const response = await apiClient.get<{ status: string; timestamp: string; version: string }>('/health');
    return response.data;
  }

  // Company Profile Management
  static async getCompanyProfile(): Promise<Company> {
    const response = await apiClient.get<Company>('/settlements/companies/profile');
    return response.data;
  }

  static async updateCompanyProfile(data: {
    companyName?: string;
    companyEmail?: string;
    companyAddress?: string;
    companyPhone?: string;
    logoUrl?: string;
  }): Promise<Company> {
    const response = await apiClient.put<Company>('/settlements/companies/profile', data);
    return response.data;
  }

  static async updateCompanyPassword(data: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ message: string }> {
    const response = await apiClient.put<{ message: string }>('/settlements/companies/profile/password', data);
    return response.data;
  }

  static async uploadCompanyLogo(file: FormData): Promise<{ logoUrl: string }> {
    const response = await apiClient.post<{ logoUrl: string }>('/settlements/companies/profile/logo', file, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Token Factory endpoints
  static async buildCreateTokenTransaction(data: {
    name: string;
    symbol: string;
    logoUrl?: string;
    initialSupply: number;
    decimals?: number;
    valueType?: string;
    walletAddress: string;
  }): Promise<{ transaction: string; mintAddress: string; metadata: any; message: string }> {
    const response = await apiClient.post<{ transaction: string; mintAddress: string; metadata: any; message: string }>(
      '/settlements/admin/tokens/create/build-transaction',
      data
    );
    return response.data;
  }

  static async saveTokenCreationResult(data: {
    name: string;
    symbol: string;
    logoUrl?: string;
    initialSupply: number;
    decimals: number;
    valueType?: string;
    mintAddress: string;
    mintAuthority: string;
    solanaSignature: string;
  }): Promise<Token> {
    const response = await apiClient.post<Token>('/settlements/admin/tokens/create/result', data);
    return response.data;
  }

  static async buildMintTokensTransaction(data: {
    tokenId: string;
    amount: number;
    destinationWallet: string;
    walletAddress: string;
  }): Promise<{ transaction: string; message: string }> {
    const response = await apiClient.post<{ transaction: string; message: string }>(
      '/settlements/admin/tokens/mint/build-transaction',
      data
    );
    return response.data;
  }

  static async saveMintTokensResult(data: {
    tokenId: string;
    amount: number;
    solanaSignature: string;
  }): Promise<Token> {
    const response = await apiClient.post<Token>('/settlements/admin/tokens/mint/result', data);
    return response.data;
  }

  static async getTokens(): Promise<Token[]> {
    const response = await apiClient.get<Token[]>('/settlements/admin/tokens/list');
    return response.data;
  }

  static async getTokenById(tokenId: string): Promise<Token> {
    const response = await apiClient.get<Token>(`/settlements/admin/tokens/${tokenId}`);
    return response.data;
  }

  static async syncTokenSupply(tokenId: string): Promise<Token> {
    const response = await apiClient.post<Token>(`/settlements/admin/tokens/${tokenId}/sync`);
    return response.data;
  }

  // Governance API methods
  static async getGovernanceStats(): Promise<any> {
    const response = await apiClient.get('/settlements/governance/stats');
    return response.data;
  }

  static async getUserVotingPower(): Promise<any> {
    const response = await apiClient.get('/settlements/governance/voting-power');
    return response.data;
  }

  static async buildLockTokensTransaction(data: {
    amount: number;
    duration: number;
  }): Promise<{ transaction: string; message: string }> {
    const response = await apiClient.post<{ transaction: string; message: string }>(
      '/settlements/governance/lock-tokens/build-transaction',
      data
    );
    return response.data;
  }

  static async saveLockResult(data: {
    amount: string;
    duration: string;
    votingPower: string;
    expiresAt: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/settlements/governance/lock-tokens/save-result',
      data
    );
    return response.data;
  }

  static async getAllLocks(): Promise<any[]> {
    const response = await apiClient.get('/settlements/governance/locks');
    return response.data;
  }

  // Analytics API methods
  static async getAnalyticsKPIs(timeRange: '24h' | '7d' | '30d' | '90d' = '24h'): Promise<AnalyticsKPIs> {
    const response = await apiClient.get(`/settlements/analytics/kpis?timeRange=${timeRange}`);
    return response.data;
  }

  static async getSettlementPerformance(timeRange: '24h' | '7d' | '30d' | '90d' = '24h'): Promise<SettlementPerformanceData[]> {
    const response = await apiClient.get(`/settlements/analytics/settlement-performance?timeRange=${timeRange}`);
    return response.data;
  }

  static async getRiskMetrics(): Promise<RiskMetrics> {
    const response = await apiClient.get('/settlements/analytics/risk-metrics');
    return response.data;
  }

  static async getApiMetrics(): Promise<ApiMetrics> {
    const response = await apiClient.get('/settlements/analytics/api-metrics');
    return response.data;
  }

  // Settlement Control API methods
  static async pauseSettlements(): Promise<any> {
    const response = await apiClient.post('/settlements/control/pause');
    return response.data;
  }

  static async resumeSettlements(): Promise<any> {
    const response = await apiClient.post('/settlements/control/resume');
    return response.data;
  }

  static async getSettlementStatus(): Promise<{ isPaused: boolean }> {
    const response = await apiClient.get('/settlements/control/status');
    return response.data;
  }

  // Export API methods
  static async exportTransactionHistory(params: {
    startDate: string;
    endDate: string;
    exportType: 'pdf' | 'csv';
  }): Promise<any> {
    if (params.exportType === 'csv') {
      const response = await apiClient.post('/settlements/export/transaction-history', params, {
        responseType: 'blob'
      });
      return response;
    } else {
      // For PDF, get JSON data
      const response = await apiClient.post('/settlements/export/transaction-history', params);
      return response.data;
    }
  }

  // API Key methods
  static async createApiKey(name?: string): Promise<ApiKeyResponse & { key: string }> {
    const response = await apiClient.post('/api-keys', { name });
    return response.data;
  }

  static async getApiKeys(): Promise<ApiKeyResponse[]> {
    const response = await apiClient.get('/api-keys');
    return response.data;
  }

  static async deleteApiKey(id: string): Promise<{ message: string }> {
    const response = await apiClient.delete(`/api-keys/${id}`);
    return response.data;
  }

  static async revokeApiKey(id: string): Promise<{ message: string }> {
    const response = await apiClient.post(`/api-keys/${id}/revoke`);
    return response.data;
  }
}

// API Key types
export interface ApiKeyResponse {
  id: string;
  keyPrefix: string;
  name: string | null;
  lastUsedAt: string | null;
  isActive: boolean;
  createdAt: string;
}

export default ApiService;
